import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Mic, MessageCircle, Zap, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

export const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Subtle galaxy background */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 opacity-60"></div>
        <div className="absolute top-20 right-10 w-72 h-72 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-20 left-10 w-72 h-72 bg-pink-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '1s' }}></div>
        
        <div className="relative max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center space-x-2 bg-white/60 backdrop-blur-sm border border-purple-200 rounded-full px-4 py-2 mb-6">
            <Sparkles size={16} className="text-purple-600" />
            <span className="text-sm font-medium text-gray-700">Powered by OpenAI Real-Time APIs</span>
          </div>
          
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 mb-6">
            The Future of
            <span className="block mt-2 bg-gradient-to-r from-purple-600 via-pink-500 to-blue-600 bg-clip-text text-transparent">
              Voice AI Communication
            </span>
          </h1>
          
          <p className="max-w-3xl mx-auto text-xl text-gray-600 mb-8">
            Nyhive delivers best-in-class, natural, and intelligent voice experiences for businesses. 
            Built on industry-leading OpenAI real-time APIs for unmatched quality and performance.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <Link to="/signup">
              <Button size="lg" className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-8 py-6 text-lg">
                Start Free Trial
                <ArrowRight size={20} className="ml-2" />
              </Button>
            </Link>
            <Link to="/pricing">
              <Button size="lg" variant="outline" className="px-8 py-6 text-lg border-2 border-gray-300 hover:border-purple-600">
                View Pricing
              </Button>
            </Link>
          </div>
          
          <p className="mt-4 text-sm text-gray-500">
            Starting at just ₹4.50/minute • No credit card required • Real-time voice intelligence
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-purple-600 mb-2">50ms</div>
              <div className="text-gray-600">Response Time</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-pink-600 mb-2">50+</div>
              <div className="text-gray-600">Languages Supported</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-blue-600 mb-2">99.9%</div>
              <div className="text-gray-600">Uptime SLA</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-purple-600 mb-2">10M+</div>
              <div className="text-gray-600">Voice Interactions</div>
            </div>
          </div>
        </div>
      </section>

      {/* What is Nyhive Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What is Nyhive?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Nyhive is a <span className="font-semibold text-purple-600">Voice AI channel</span> that brings the power of 
              OpenAI's cutting-edge real-time APIs to your business. Experience natural, intelligent conversations 
              that feel human, powered by the world's most advanced AI technology.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mic className="text-purple-600" size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Real-Time Processing</h3>
                  <p className="text-gray-600">Industry-leading latency with OpenAI's real-time APIs for seamless voice interactions.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="text-pink-600" size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Natural Conversations</h3>
                  <p className="text-gray-600">AI that understands context, tone, and intent for truly human-like interactions.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Zap className="text-blue-600" size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Easy Integration</h3>
                  <p className="text-gray-600">Simple APIs and SDKs to get Voice AI running in your app within minutes.</p>
                </div>
              </div>
            </div>
            
            <Card className="border-2 shadow-xl">
              <CardContent className="p-8">
                <div className="bg-gradient-to-br from-purple-600 via-pink-600 to-blue-600 rounded-lg p-8 text-white">
                  <h3 className="text-2xl font-bold mb-4">Why Choose Nyhive?</h3>
                  <ul className="space-y-3">
                    <li className="flex items-center space-x-2">
                      <span className="text-yellow-300">✓</span>
                      <span>Powered by OpenAI's latest technology</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <span className="text-yellow-300">✓</span>
                      <span>Best-in-class voice quality</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <span className="text-yellow-300">✓</span>
                      <span>Startup-friendly pricing</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <span className="text-yellow-300">✓</span>
                      <span>Enterprise-grade security</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <span className="text-yellow-300">✓</span>
                      <span>99.9% uptime guarantee</span>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Features Highlight */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Voice AI Capabilities</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Everything you need to deliver exceptional voice experiences to your customers.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="border-2 hover:border-purple-300 transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                  <Mic className="text-purple-600" size={24} />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Real-Time Voice AI</h3>
                <p className="text-gray-600">
                  Minimal latency with OpenAI's real-time APIs for natural, flowing conversations.
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-2 hover:border-pink-300 transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center mb-4">
                  <Globe className="text-pink-600" size={24} />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Multi-Language</h3>
                <p className="text-gray-600">
                  Support 50+ languages with accurate recognition and natural synthesis.
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-2 hover:border-blue-300 transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <Zap className="text-blue-600" size={24} />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Easy Integration</h3>
                <p className="text-gray-600">
                  Simple REST APIs and SDKs to integrate Voice AI in minutes, not weeks.
                </p>
              </CardContent>
            </Card>
          </div>
          
          <div className="text-center mt-12">
            <Link to="/features">
              <Button variant="outline" size="lg" className="border-2 hover:border-purple-600">
                Explore All Features
                <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-600 via-pink-600 to-blue-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to experience Voice AI?</h2>
          <p className="text-xl mb-8 text-purple-100">
            Start delivering intelligent, natural voice experiences to your customers with Nyhive.
          </p>
          <Link to="/signup">
            <Button size="lg" className="bg-white text-purple-600 hover:bg-gray-100 px-8 py-6 text-lg font-semibold">
              Start Your Free Trial
              <ArrowRight size={20} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};